import { useCallback, useContext } from "react";
import { ZoomContext } from "../../zoom/zoom-context";

interface HostControlsApi {
  muteAll: () => Promise<void>;
  remove: (userId: number) => Promise<void>;
  /** Mute one participant. Takes effect immediately. */
  muteParticipant: (userId: number) => Promise<void>;
  /** Zoom forbids unmuting someone directly; this sends them a consent
   *  prompt, which surfaces on their client as `host-ask-unmute-audio`. */
  askToUnmute: (userId: number) => Promise<void>;
}

/** Host-only side-effects on top of the Video SDK. */
export function useHostControls(isHost: boolean): HostControlsApi {
  const zmClient = useContext(ZoomContext);

  const muteAll = useCallback(async () => {
    if (!isHost) return;
    const stream: any = zmClient?.getMediaStream?.();
    try {
      await stream?.muteAllAudio?.();
    } catch {
      /* ignore */
    }
  }, [zmClient, isHost]);

  const remove = useCallback(
    async (userId: number) => {
      if (!isHost) return;
      try {
        await zmClient?.removeUser?.(userId);
      } catch {
        /* ignore */
      }
    },
    [zmClient, isHost]
  );

  const muteParticipant = useCallback(
    async (userId: number) => {
      if (!isHost) return;
      const stream: any = zmClient?.getMediaStream?.();
      try {
        await stream?.muteAudio?.(userId);
      } catch {
        /* ignore */
      }
    },
    [zmClient, isHost]
  );

  const askToUnmute = useCallback(
    async (userId: number) => {
      if (!isHost) return;
      const stream: any = zmClient?.getMediaStream?.();
      try {
        await stream?.unmuteAudio?.(userId);
      } catch {
        /* ignore */
      }
    },
    [zmClient, isHost]
  );

  return { muteAll, remove, muteParticipant, askToUnmute };
}
